import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useMemo, useCallback, useRef } from 'react';
import { FaLink } from 'react-icons/fa';
import checkModifierKeyActivated from '../../../data/utilities/checkModifierKeyActivated';
import ContextMenu from '../../elements/ContextMenu';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import useTableSortBy from '../../hooks/useTableSortBy';
import ZonesTableRow from './ZonesTableRow';
const tableStyle = css `
  border-spacing: 0;
  border: 1px solid #dedede;
  width: 100%;
  font-size: 12px;

  .react-contextmenu-wrapper {
    display: contents;
  }

  th {
    position: sticky;
    background-color: white;
    z-index: 2;
  }

  th,
  td {
    white-space: nowrap;
    text-align: center;
    margin: 0;
    padding: 0.1rem 0.4rem;
    border-bottom: 1px solid #dedede;
    border-right: 1px solid #dedede;

    :last-child {
      border-right: 0;
    }

    button {
      background-color: transparent;
      border: none;
      padding: 0 5px;
    }
  }

  tr {
    :last-child {
      td {
        border-bottom: 0;
      }
    }
  }

  thead tr:nth-of-type(1) th {
    top: 0;
  }

  thead tr:nth-of-type(2) th {
    top: 21px;
  }
`;
function ZonesTable({ tableData, onUnlink, context, nuclei, experiment, }) {
    const contextRef = useRef(null);
    const contextMenuHandler = useCallback((e, rowData) => {
        if (!checkModifierKeyActivated(e)) {
            e.preventDefault();
            contextRef.current.handleContextMenu(e, rowData);
        }
    }, [contextRef]);
    const data = useMemo(() => {
        const data = [];
        for (const [i, zone] of tableData.entries()) {
            if (zone.signals.length === 1) {
                data.push({
                    ...zone,
                    tableMetaInfo: {
                        ...zone.tableMetaInfo,
                        signal: zone.signals[0],
                        rowIndex: i,
                        signalIndex: 0,
                        id: zone.signals[0].id,
                        experiment,
                        nuclei,
                    },
                });
            }
            else if (zone.signals.length > 1) {
                for (const [j, signal] of zone.signals.entries()) {
                    let hide = false;
                    let rowSpan = null;
                    if (j < zone.signals.length - 1) {
                        if (j === 0) {
                            rowSpan = zone.signals.length;
                        }
                        else {
                            hide = true;
                        }
                    }
                    else {
                        hide = true;
                    }
                    data.push({
                        ...zone,
                        tableMetaInfo: {
                            ...zone.tableMetaInfo,
                            signal,
                            rowSpan,
                            hide,
                            rowIndex: i,
                            signalIndex: j,
                            id: signal.id,
                            experiment,
                        },
                    });
                }
            }
        }
        return data;
    }, [experiment, nuclei, tableData]);
    const { items: sortedData, isSortedDesc, onSort } = useTableSortBy(data);
    const { deltaPPM: deltaX } = usePanelPreferences('zones', nuclei[0]);
    const { deltaPPM: deltaY } = usePanelPreferences('zones', nuclei[1]);
    const rows = useMemo(() => sortedData.map((rowData, index) => (_jsx(ZonesTableRow, { rowIndex: index, rowData: rowData, onUnlink: onUnlink, onContextMenu: (e, rowData) => contextMenuHandler(e, rowData), format: { x: deltaX.format, y: deltaY.format } }, `${rowData.tableMetaInfo.id}`))), [sortedData, onUnlink, deltaX.format, deltaY.format, contextMenuHandler]);
    return (_jsxs("div", { children: [_jsxs("table", { css: tableStyle, children: [_jsxs("thead", { children: [_jsxs("tr", { children: [_jsx("th", { rowSpan: 2, children: "#" }), _jsx("th", { colSpan: 2, children: "\u03B4 (ppm)" }), _jsx("th", { colSpan: 2, children: _jsx(FaLink, {}) }), _jsx("th", { colSpan: 2, children: "\u03A3" }), _jsx("th", { rowSpan: 2, children: "Kind" }), _jsx("th", { rowSpan: 2, children: '' })] }), _jsxs("tr", { children: [_jsx("th", { id: "tableMetaInfo.signal.x.delta", ...onSort, children: _jsxs(SurroundedText, { text: "F2", children: [nuclei[0], ' ', isSortedDesc('tableMetaInfo.signal.x.delta').content] }) }), _jsx("th", { id: "tableMetaInfo.signal.y.delta", ...onSort, children: _jsxs(SurroundedText, { text: "F1", children: [nuclei[1], ' ', isSortedDesc('tableMetaInfo.signal.y.delta').content] }) }), _jsx("th", { children: _jsx(SurroundedText, { text: "F2", children: nuclei[0] }) }), _jsx("th", { children: _jsx(SurroundedText, { text: "F1", children: nuclei[1] }) }), _jsx("th", { children: _jsx(SurroundedText, { text: "F2", children: nuclei[0] }) }), _jsx("th", { children: _jsx(SurroundedText, { text: "F1", children: nuclei[1] }) })] })] }), _jsx("tbody", { children: rows })] }), _jsx(ContextMenu, { ref: contextRef, context: context })] }));
}
function SurroundedText(props) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                    position: 'absolute',
                    fontSize: 8,
                    color: '#3539E6',
                }, children: props.text }), _jsx("div", { style: { marginLeft: 5 }, children: props.children })] }));
}
export default ZonesTable;
